import React, { useMemo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import {
  Card,
  Grid,
  Stack,
  Box,
  Typography,
  Chip,
  Link,
  Collapse,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { styled, useTheme } from "@mui/material/styles";
import { formatDistanceToNow } from "date-fns";
import { fmtDuration } from "./ChargerUptimeV2";
import { fEnergy, fMoneyShort } from "src/utils/formatNumber";
import { hideColumnsOnPhone, hideColumnsBelow } from "./tableSx";
import { connectorStatusWhenOffline } from "src/utils/connectorStatus";

// Charger + status summary at the top of the dashboard. Status tiles filter the
// list; each row expands to reveal connector states, firmware and utilization.

const isOnline = (charger) => charger?.status === "Active";
const hasFault = (charger) => Boolean(charger?.isFaulted);

// A charger is "In Use" when any connector is actively charging.
// Only an online charger can be charging: an offline one's stored connector
// status is stale (it shows as Offline).
const isCharging = (charger) =>
  isOnline(charger) &&
  (Array.isArray(charger?.connectors) ? charger.connectors : []).some(
    (c) => String(c?.status || "").toUpperCase() === "CHARGING"
  );

// Three mutually-exclusive states: In Use > Online (available) > Offline.
const chargerState = (charger) =>
  isCharging(charger) ? "inuse" : isOnline(charger) ? "online" : "offline";

const stateMeta = (theme) => ({
  inuse: { label: "In Use", color: theme.palette.info.main },
  online: { label: "Online", color: theme.palette.success.main },
  offline: { label: "Offline", color: theme.palette.error.main },
});
const STATE_RANK = { offline: 0, online: 1, inuse: 2 };
const sessions30d = (charger) => Number(charger?.utilization?.sessions) || 0;
// "Needs attention" = offline, a fault/error, or online but unused in the last 30 days.
export const needsAttention = (charger) =>
  !isOnline(charger) || hasFault(charger) || sessions30d(charger) === 0;

// OCPP connector state colors.
const connectorStatusColor = (status, theme) => {
  const colors = {
    AVAILABLE: theme.palette.success.main,
    CHARGING: theme.palette.info.main,
    PREPARING: theme.palette.warning.main,
    FINISHING: theme.palette.warning.main,
    SUSPENDEDEV: theme.palette.warning.main,
    SUSPENDEDEVSE: theme.palette.warning.main,
    RESERVED: theme.palette.info.light,
    UNAVAILABLE: theme.palette.text.secondary,
    FAULTED: theme.palette.error.main,
    OFFLINE: theme.palette.error.main,
    MAINTENANCE: theme.palette.violet.main,
  };
  return colors[String(status || "").toUpperCase()] || theme.palette.text.secondary;
};

const connectorList = (charger) => {
  const connectors = Array.isArray(charger?.connectors) ? charger.connectors : [];
  // Offline charger: a stale Available / in-use status shows as Offline; others
  // (Faulted, …) as stored (connectorStatusWhenOffline; the API does the same).
  const offline = !isOnline(charger);
  return connectors.map((c, i) => {
    const kw = parseFloat(c?.kw);
    const status = offline ? connectorStatusWhenOffline(c?.status) : c?.status;
    return {
      label: `Connector ${i + 1}`,
      power: !isNaN(kw) && kw > 0 ? `${kw} kW` : "—",
      status: status || null,
      errorCode: c?.errorCode || null,
      errorInfo: c?.errorInfo || null,
    };
  });
};

const chargerId = (charger) =>
  charger?.chargepoint_id ||
  charger?.charge_box_id ||
  (charger?.id != null ? String(charger.id) : "—");

const chargerSerial = (charger) =>
  charger?.chargePointSerialNumber || charger?.chargeBoxSerialNumber || "—";

const chargerCityState = (charger) =>
  [charger?.city, charger?.state].filter(Boolean).join(", ") || "—";

const chargerFullAddress = (charger) =>
  [charger?.street, charger?.houseNumber, charger?.city, charger?.state, charger?.zipCode, charger?.country]
    .filter(Boolean)
    .join(", ") || "Address not available";

const parseServerDate = (value) => {
  if (!value) return null;
  let s = String(value).replace(" ", "T").replace(/(\.\d{3})\d+/, "$1");
  if (!s.includes("Z") && !s.includes("+")) s += "Z";
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const relLabel = (value, emptyTitle) => {
  const d = parseServerDate(value);
  if (!d) return { text: "Never", title: emptyTitle };
  return { text: `${formatDistanceToNow(d)} ago`, title: d.toLocaleString() };
};

const healthInfo = (charger, theme) => {
  // Offline first: a dead charger is never "OK", and any old fault is stale.
  if (!isOnline(charger)) {
    const since = parseServerDate(charger?.lastHeartbeatTimestamp);
    return {
      label: "Offline",
      // Shown after the label from tablet width up; phones have no room for it.
      detail: since ? fmtDuration((Date.now() - since.getTime()) / 1000) : null,
      color: theme.palette.error.main,
      sev: 3,
      title: since ? `No heartbeat since ${since.toLocaleString()}` : "No heartbeat recorded",
    };
  }
  if (hasFault(charger)) {
    const err = charger?.lastError || {};
    const code = err.errorCode;
    const title =
      [code, err.errorInfo].filter(Boolean).join(" — ") +
      (err.connectorId ? ` (Connector ${err.connectorId})` : "");
    return { label: code || "Faulted", color: theme.palette.error.main, sev: 2, title: title || "Faulted" };
  }
  if (sessions30d(charger) === 0) {
    return {
      label: "Idle",
      color: theme.palette.warning.main,
      sev: 1,
      title: "Online but no charging sessions in the last 30 days",
    };
  }
  return { label: "OK", color: theme.palette.success.main, sev: 0, title: "No faults reported" };
};

const totalKw = (charger) =>
  (Array.isArray(charger?.connectors) ? charger.connectors : []).reduce(
    (sum, c) => sum + (parseFloat(c?.kw) || 0),
    0
  );

const StatTile = styled(Card, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "accent",
})(({ theme, active, accent }) => ({
  cursor: "pointer",
  padding: theme.spacing(1.75),
  borderRadius: theme.shape.borderRadiusMd,
  border: `2px solid ${active ? accent : theme.palette.grey[200]}`,
  background: active ? `${accent}12` : theme.palette.background.paper,
  boxShadow: active ? theme.customShadows.z8 : theme.customShadows.z1,
  transition: "all 0.2s ease",
  height: "100%",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.customShadows.z8,
    borderColor: accent,
  },
  "&:focus-visible": {
    outline: `2px solid ${accent}`,
    outlineOffset: 2,
  },
}));

const tiles = (theme) => [
  { key: "all", label: "Total Chargers", accent: theme.palette.text.secondary, hint: "All chargers" },
  { key: "inuse", label: "In Use", accent: theme.palette.info.main, hint: "Currently charging (subset of Online)" },
  { key: "online", label: "Online", accent: theme.palette.success.main, hint: "Connected chargers (includes In Use)" },
  { key: "offline", label: "Offline", accent: theme.palette.error.main, hint: "Not connected" },
  {
    key: "attention",
    label: "Needs Attention",
    accent: theme.palette.warning.main,
    hint: "Offline, faulted, or online but idle (no sessions in the last 30 days)",
  },
];

const SORT_COLUMNS = {
  id: { label: "Charger ID", type: "string", value: (c) => chargerId(c) },
  serial: { label: "Serial Number", type: "string", value: (c) => chargerSerial(c) },
  status: { label: "Status", type: "number", value: (c) => STATE_RANK[chargerState(c)] },
  health: { label: "Health", type: "number", value: (c, theme) => healthInfo(c, theme).sev },
  power: { label: "Connector Power", type: "number", value: (c) => totalKw(c) },
  lastCharged: {
    // When the charger last ran a charging session.
    label: "Last Session",
    type: "number",
    value: (c) => {
      const d = parseServerDate(c?.lastChargedAt);
      return d ? d.getTime() : null;
    },
  },
  lastConnected: {
    label: "Last Connected",
    type: "number",
    value: (c) => {
      const d = parseServerDate(c?.lastHeartbeatTimestamp);
      return d ? d.getTime() : null;
    },
  },
  location: { label: "Location", type: "string", value: (c) => chargerCityState(c) },
};

const HEADER_COLUMNS = [
  "id",
  "serial",
  "status",
  "health",
  "power",
  "lastCharged",
  "lastConnected",
  "location",
];
const COL_SPAN = HEADER_COLUMNS.length + 1; // +1 for the expand toggle
// 5 rows by default so the list stays compact on the dashboard.
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

// On phones keep the columns that matter (ID, Status, Health); the rest are in
// the expanded row. Positions are 1-based, the toggle is 1.
const PHONE_HIDDEN_COLUMNS = ["serial", "power", "lastCharged", "lastConnected", "location"].map(
  (col) => HEADER_COLUMNS.indexOf(col) + 2
);
// Below extra-wide screens (xl) the full table doesn't fit the card either: Serial
// moves to the expanded row too.
const NARROW_HIDDEN_COLUMNS = ["serial"].map((col) => HEADER_COLUMNS.indexOf(col) + 2);
const phoneHiddenSx = {
  ...hideColumnsBelow(NARROW_HIDDEN_COLUMNS, "xl"),
  ...hideColumnsOnPhone(PHONE_HIDDEN_COLUMNS.filter((n) => !NARROW_HIDDEN_COLUMNS.includes(n))),
  "& .MuiTableCell-root": { px: { xs: 0.75, sm: 2 } },
};

const isEmptyVal = (v) => v === null || v === undefined || v === "" || v === "—";

// Persist the panel's filter/sort/page so it survives leaving and returning.
// Keyed PER TEAM so switching company/team never inherits another team's filter
// (e.g. landing on "Needs Attention" with 0 matches and showing an empty list).
// Versioned: bumped when a default changes (v2: 5 rows; v3: Online filter) so views
// saved with the old defaults are dropped once.
const PANEL_STATE_KEY = "cms_v2_charger_panel_v3";
const VALID_FILTERS = ["all", "inuse", "online", "offline", "attention"];
const panelKeyFor = (teamId) =>
  teamId || teamId === 0 ? `${PANEL_STATE_KEY}_${teamId}` : PANEL_STATE_KEY;
const loadPanelState = (teamId) => {
  try {
    return JSON.parse(window.localStorage.getItem(panelKeyFor(teamId))) || {};
  } catch (_) {
    return {};
  }
};
// Coerce a stored blob into a valid, defaulted panel state.
const normalizePanelState = (p = {}) => ({
  // Online chargers by default.
  selected: VALID_FILTERS.includes(p.selected) ? p.selected : "online",
  page: Number.isInteger(p.page) ? p.page : 0,
  rowsPerPage: ROWS_PER_PAGE_OPTIONS.includes(p.rowsPerPage) ? p.rowsPerPage : ROWS_PER_PAGE_OPTIONS[0],
  orderBy: SORT_COLUMNS[p.orderBy] ? p.orderBy : "id",
  order: p.order === "desc" ? "desc" : "asc",
});

const comparator = (column, order, theme) => (a, b) => {
  const { type, value } = SORT_COLUMNS[column];
  const va = value(a, theme);
  const vb = value(b, theme);
  const aEmpty = isEmptyVal(va);
  const bEmpty = isEmptyVal(vb);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  let cmp;
  if (type === "string") {
    cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
  } else {
    cmp = va < vb ? -1 : va > vb ? 1 : 0;
  }
  return order === "asc" ? cmp : -cmp;
};

const Metric = ({ label, value, color, onClick, title }) => (
  <Box>
    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
      {label}
    </Typography>
    {onClick ? (
      <Tooltip title={title || ""} arrow placement="top">
        <Typography
          component="button"
          type="button"
          onClick={onClick}
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: color || "primary.main",
            background: "none",
            border: "none",
            p: 0,
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: 2,
            "&:hover": { opacity: 0.8 },
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    ) : (
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: color || "text.primary" }}>
        {value}
      </Typography>
    )}
  </Box>
);

const ChargerDetail = ({ charger, onFailedRateClick, theme }) => {
  const connectors = connectorList(charger);
  const u = charger?.utilization || {};
  return (
    <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
            CONNECTORS
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {connectors.length === 0 && (
              <Typography variant="body2" sx={{ color: "text.disabled" }}>
                No connectors
              </Typography>
            )}
            {connectors.map((c, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: connectorStatusColor(c.status, theme), flexShrink: 0 }} />
                <Typography variant="body2" sx={{ minWidth: 92 }}>
                  {c.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60 }}>
                  {c.power}
                </Typography>
                <Typography variant="body2" sx={{ color: connectorStatusColor(c.status, theme), fontWeight: 600 }}>
                  {c.status || "Unknown"}
                </Typography>
                {c.errorCode && (
                  <Tooltip title={c.errorInfo || ""} arrow>
                    <Chip size="small" color="error" variant="outlined" label={c.errorCode} />
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>
        </Grid>

        <Grid item xs={12} md={3}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
            DEVICE
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            <Metric label="Vendor" value={charger?.chargePointVendor || "—"} />
            <Metric label="Firmware" value={charger?.fwVersion || "—"} />
            {/* Columns hidden on narrower screens show here instead. */}
            <Box sx={{ display: { xs: "block", xl: "none" } }}>
              <Metric label="Serial" value={chargerSerial(charger)} />
            </Box>
            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Metric label="Last Session" value={relLabel(charger?.lastChargedAt, "").text} />
                <Metric label="Location" value={chargerCityState(charger)} />
                <Metric label="Last Connected" value={relLabel(charger?.lastHeartbeatTimestamp, "").text} />
              </Stack>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
            UTILIZATION (LAST 30 DAYS)
          </Typography>
          <Grid container spacing={1} sx={{ mt: 0 }}>
            <Grid item xs={4}><Metric label="Sessions" value={u.sessions ?? 0} /></Grid>
            <Grid item xs={4}><Metric label="Energy" value={fEnergy(u.energy)} /></Grid>
            <Grid item xs={4}><Metric label="Revenue" value={fMoneyShort(u.revenue)} /></Grid>
            <Grid item xs={4}><Metric label="Utilization" value={`${Number(u.utilizationPct || 0).toFixed(1)}%`} /></Grid>
            <Grid item xs={4}>
              <Metric
                label="Failed rate"
                value={`${Number(u.failedRate || 0).toFixed(1)}%`}
                color={Number(u.failedRate || 0) > 15 ? theme.palette.error.main : undefined}
                onClick={onFailedRateClick ? () => onFailedRateClick(charger) : undefined}
                title="View this charger's failed transactions"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

const ChargerStatusPanel = ({ chargers, isFetching = false, teamId, embedded = false }) => {
  const theme = useTheme();
  const TILES = tiles(theme);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  // Carry the current URL so the detail page's back button returns here.
  const returnTo = `${location.pathname}${location.search}`;
  const openCharger = (pk) => navigate(`/charger/${pk}`, { state: { returnTo } });

  // Open the failed transactions for ONE charger: failed sessions (no energy)
  // for that chargepoint over the widest window.
  const openFailedTransactions = (charger) => {
    const id = chargerId(charger);
    if (!id || id === "—") return;
    // Deep-link to the Transactions page's Failed tab, pre-filtered to this
    // charger over the widest window.
    navigate(
      `/transactions?tab=failed&frange=last90days&fcharger=${encodeURIComponent(id)}`
    );
  };

  // Open a faulted charger directly on its Status History (connector status) tab,
  // preselecting the faulted connector.
  const openConnectorStatus = (charger) => {
    if (charger?.chargepoint_pk == null) return;
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: charger.chargepoint_id || "",
        tabOpenedFor: "charger",
        tabOpened: "status_history",
      })
    );
    navigate(`/charger/${charger.chargepoint_pk}`, {
      state: { returnTo, statusConnectorId: charger?.lastError?.connectorId ?? null },
    });
  };
  // Initial state from THIS team's saved blob (per-team key).
  const initial = useMemo(() => normalizePanelState(loadPanelState(teamId)), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [selected, setSelected] = useState(initial.selected);
  const [page, setPage] = useState(initial.page);
  const [rowsPerPage, setRowsPerPage] = useState(initial.rowsPerPage);
  const [orderBy, setOrderBy] = useState(initial.orderBy);
  const [order, setOrder] = useState(initial.order);
  const [expanded, setExpanded] = useState(() => new Set());

  // When the selected team changes, load THAT team's saved view (or defaults),
  // so a company never inherits another's filter. Skips the initial mount.
  const prevTeamRef = useRef(teamId);
  useEffect(() => {
    if (prevTeamRef.current === teamId) return;
    prevTeamRef.current = teamId;
    const s = normalizePanelState(loadPanelState(teamId));
    setSelected(s.selected);
    setPage(s.page);
    setRowsPerPage(s.rowsPerPage);
    setOrderBy(s.orderBy);
    setOrder(s.order);
    setExpanded(new Set());
  }, [teamId]);

  // Save this team's filter/sort/page so returning restores the view.
  useEffect(() => {
    try {
      window.localStorage.setItem(
        panelKeyFor(teamId),
        JSON.stringify({ selected, page, rowsPerPage, orderBy, order })
      );
    } catch (_) {
      /* storage unavailable - ignore */
    }
  }, [teamId, selected, page, rowsPerPage, orderBy, order]);

  const list = useMemo(() => (Array.isArray(chargers) ? chargers : []), [chargers]);

  const counts = useMemo(() => {
    let inuse = 0;
    let online = 0; // all Active chargers — INCLUDES In Use
    list.forEach((c) => {
      if (isOnline(c)) {
        online += 1;
        if (isCharging(c)) inuse += 1;
      }
    });
    return {
      all: list.length,
      inuse,
      online,
      offline: list.length - online,
      attention: list.filter(needsAttention).length,
    };
  }, [list]);

  const filtered = useMemo(() => {
    let base = list;
    // In Use is a subset of Online; the Online filter shows all Active chargers.
    if (selected === "inuse") base = list.filter((c) => isOnline(c) && isCharging(c));
    else if (selected === "online") base = list.filter(isOnline);
    else if (selected === "offline") base = list.filter((c) => !isOnline(c));
    else if (selected === "attention") base = list.filter(needsAttention);
    return [...base].sort(comparator(orderBy, order, theme));
  }, [list, selected, orderBy, order, theme]);

  const handleSort = (column) => {
    if (orderBy === column) setOrder((p) => (p === "asc" ? "desc" : "asc"));
    else {
      setOrderBy(column);
      setOrder("asc");
    }
    setPage(0);
  };

  const handleSelect = (key) => {
    setSelected(key);
    setPage(0);
  };

  const toggleExpand = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Guard against a stale page (persisted from another company/filter with more
  // rows) landing past the end of the current list — which would slice to an
  // empty page even though the counts show rows. Clamp to the last valid page.
  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const paged = filtered.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage
  );

  // `embedded`: rendered inside another card (the merged Chargers section), so
  // skip our own card chrome and heading.
  const Root = embedded ? Box : Card;
  const rootSx = embedded
    ? {}
    : {
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: 2,
        boxShadow: (theme) => theme.customShadows.z4,
        border: (theme) => `1px solid ${theme.palette.grey[200]}`,
      };

  return (
    <Root sx={rootSx}>
      {!embedded && (
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Chargers & Status
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {TILES.map((tile) => (
          <Grid item xs={tile.key === "attention" ? 12 : 6} sm={4} md={2.4} key={tile.key}>
            <Tooltip title={tile.hint || ""} arrow placement="top">
              <StatTile
                active={selected === tile.key}
                accent={tile.accent}
                onClick={() => handleSelect(tile.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(tile.key);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={selected === tile.key}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                    {tile.label}
                  </Typography>
                  {isFetching ? (
                    <CircularProgress size={26} />
                  ) : (
                    <Typography variant="h3" sx={{ fontWeight: 700, color: tile.accent }}>
                      {counts[tile.key]}
                    </Typography>
                  )}
                </Stack>
              </StatTile>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      <TableContainer>
        <Table size="small" aria-label="charger list" sx={phoneHiddenSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              {HEADER_COLUMNS.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600 }} sortDirection={orderBy === col ? order : false}>
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : "asc"}
                    onClick={() => handleSort(col)}
                  >
                    {SORT_COLUMNS[col].label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={COL_SPAN} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COL_SPAN} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {list.length > 0 && selected !== "all"
                    ? `No chargers match the "${
                        TILES.find((t) => t.key === selected)?.label || selected
                      }" filter — click Total Chargers to see all.`
                    : "No chargers to show"}
                </TableCell>
              </TableRow>
            ) : (
              paged.map((charger) => {
                const powers = connectorList(charger);
                const key = charger.chargepoint_pk ?? charger.chargepoint_id ?? chargerSerial(charger);
                const isExp = expanded.has(key);
                const health = healthInfo(charger, theme);
                const lastCharged = relLabel(charger?.lastChargedAt, "No charging sessions recorded");
                const lastConnected = relLabel(charger?.lastHeartbeatTimestamp, "No heartbeat recorded");
                return (
                  <React.Fragment key={key}>
                    <TableRow hover>
                      <TableCell sx={{ width: 40 }}>
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(key)}
                          aria-label="expand charger"
                          sx={{ width: { xs: 40, sm: 30 }, height: { xs: 40, sm: 30 } }}
                        >
                          {isExp ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {charger?.chargepoint_pk != null ? (
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => openCharger(charger.chargepoint_pk)}
                            sx={{ fontWeight: 600, textAlign: "left", cursor: "pointer" }}
                          >
                            {chargerId(charger)}
                          </Link>
                        ) : (
                          chargerId(charger)
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Tooltip title={chargerSerial(charger)} arrow placement="top">
                          <Box
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {chargerSerial(charger)}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const meta = stateMeta(theme)[chargerState(charger)];
                          return (
                            <Chip
                              size="small"
                              label={meta.label}
                              sx={{ fontWeight: 600, color: meta.color, bgcolor: `${meta.color}18` }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            health.sev === 2
                              ? `${health.title} — click to view connector status`
                              : health.title
                          }
                          arrow
                          placement="top"
                        >
                          <Chip
                            size="small"
                            label={
                              health.detail ? (
                                <>
                                  {health.label}
                                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                                    {` · ${health.detail}`}
                                  </Box>
                                </>
                              ) : (
                                health.label
                              )
                            }
                            clickable={health.sev === 2}
                            onClick={health.sev === 2 ? () => openConnectorStatus(charger) : undefined}
                            sx={{
                              fontWeight: 600,
                              color: health.color,
                              bgcolor: `${health.color}18`,
                              maxWidth: 160,
                              cursor: health.sev === 2 ? "pointer" : "help",
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {powers.length > 0 ? (
                          <Stack spacing={0.25}>
                            {powers.map((c, i) => (
                              <Typography key={i} variant="caption" sx={{ whiteSpace: "nowrap" }}>
                                {c.label}: <strong>{c.power}</strong>
                              </Typography>
                            ))}
                          </Stack>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={lastCharged.title} arrow placement="top">
                          <Typography
                            variant="caption"
                            sx={{ whiteSpace: "nowrap", cursor: "help", color: charger?.lastChargedAt ? "text.primary" : "text.disabled" }}
                          >
                            {lastCharged.text}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={lastConnected.title} arrow placement="top">
                          <Typography
                            variant="caption"
                            sx={{ whiteSpace: "nowrap", cursor: "help", color: charger?.lastHeartbeatTimestamp ? "text.primary" : "text.disabled" }}
                          >
                            {lastConnected.text}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={chargerFullAddress(charger)} arrow placement="top">
                          <span style={{ cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 2 }}>
                            {chargerCityState(charger)}
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={COL_SPAN} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isExp} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5 }}>
                            <ChargerDetail charger={charger} onFailedRateClick={openFailedTransactions} theme={theme} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {filtered.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={filtered.length}
          page={safePage}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      )}
    </Root>
  );
};

ChargerStatusPanel.propTypes = {
  chargers: PropTypes.array,
  isFetching: PropTypes.bool,
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  embedded: PropTypes.bool,
};

export default ChargerStatusPanel;
